import React from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import SaveIcon from '@mui/icons-material/Save';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import type { BDirectRow } from '../../../hooks/useBDirectRows';
import {
  AddRow,
  CellFooterLink,
  CellFooterLinks,
  ColBuild,
  ColCurrent,
  ColOwner,
  ColPreprodRegion,
  ColRegion,
  ColState,
  ColStep,
  DangerOutlinedButton,
  EditTextButton,
  Empty,
  HeaderActions,
  LockedBanner,
  OwnerMissing,
  RegionDash,
  RegionHeader,
  RowParkedMessage,
  SectionCard,
  SectionHeader,
  SectionRow,
  SectionTitle,
  SectionTitleColumn,
  SectionTitleField,
  StepActionsRow,
  StepHtml,
  Table,
} from './styles';
import {
  EditProps,
  PREPROD_REGION_COLUMNS,
  REGION_COLUMNS,
  SectionCardProps,
} from './types';
import { StateSelect } from './StateSelect';
import { BuildCell } from './BuildCell';
import { RegionCell } from './RegionCell';
import { PreprodRegionCell } from './PreprodRegionCell';
import { CurrentCell } from './CurrentCell';
import { rowDisplayMeta } from '../releaseTag';

export const BDirectSectionCard: React.FC<SectionCardProps> = props => {
  const { release, env, section, steps } = props;
  const editing = props.mode === 'edit';
  const lockedByOther =
    props.mode === 'view' &&
    !!props.lockedBy &&
    props.lockedBy !== props.currentUser;

  // Header content varies between modes:
  //   view  → Typography title + Edit button (or Locked banner if other holds it)
  //   edit  → TextField title + Save / Cancel / Delete buttons
  const header = (
    <SectionHeader>
      {editing ? (
        <SectionTitleField>
          <TextField
            value={props.sectionText}
            onChange={e => props.onUpdateSectionText(e.target.value)}
            placeholder="Section title"
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{ style: { fontWeight: 600 } }}
          />
        </SectionTitleField>
      ) : (
        <SectionTitleColumn>
          <SectionTitle>{section.text || '(Untitled section)'}</SectionTitle>
          {section.updatedBy && (
            <div>
              updated by <EntityRefLink entityRef={section.updatedBy} />
            </div>
          )}
        </SectionTitleColumn>
      )}
      <HeaderActions>
        {props.mode === 'view' && !lockedByOther && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={props.onEdit}
            disabled={props.editPending || props.editDisabled}
            title={
              props.editDisabled
                ? 'Finish editing the other section first (Save or Cancel) before editing this one.'
                : undefined
            }
          >
            {props.editPending ? 'Locking…' : 'Edit Section'}
          </Button>
        )}
        {props.mode === 'edit' && (
          <>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={props.onSave}
              disabled={props.savePending}
            >
              {props.savePending ? 'Saving…' : 'Save Section'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={props.onCancel}
              disabled={props.cancelPending}
            >
              {props.cancelPending ? 'Cancelling…' : 'Cancel'}
            </Button>
            <DangerOutlinedButton
              size="small"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={props.onDelete}
              disabled={props.deletePending}
            >
              {props.deletePending ? 'Deleting…' : 'Delete section'}
            </DangerOutlinedButton>
          </>
        )}
      </HeaderActions>
    </SectionHeader>
  );

  // Step cell content shared across modes. In edit mode an extra row of
  // action buttons (Edit step + move up/down + remove) appears below the
  // footer links.
  const renderStepCell = (
    row: BDirectRow,
    isFirst: boolean,
    isLast: boolean,
  ) => (
    <>
      <StepHtml
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: row.text || '<em style="opacity:0.5">(empty step)</em>',
        }}
      />
      <CellFooterLinks>
        {row.component && (
          <CellFooterLink>
            <EntityRefLink entityRef={row.component} />
          </CellFooterLink>
        )}
      </CellFooterLinks>
      {editing && (
        <StepActionsRow>
          <EditTextButton
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => (props as EditProps).onOpenStepEditor(row.id)}
          >
            Edit step
          </EditTextButton>
          <IconButton
            size="small"
            onClick={() => (props as EditProps).onMoveStep(row.id, -1)}
            disabled={isFirst}
            aria-label="Move up"
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => (props as EditProps).onMoveStep(row.id, 1)}
            disabled={isLast}
            aria-label="Move down"
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => (props as EditProps).onRemoveStep(row.id)}
            aria-label="Remove step"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </StepActionsRow>
      )}
    </>
  );

  const body =
    steps.length === 0 && !editing ? (
      <Empty>
        No rows yet — added later or via manual step (section Edit mode).
      </Empty>
    ) : (
      <Table>
        {/* `table-layout: fixed` (set on the Table styled component)
            consumes these widths so every section's table shares the
            same column geometry. Both modes use the same colgroup so
            flipping a section into edit mode doesn't reshape the table. */}
        <colgroup>
          <ColStep />
          <ColState />
          <ColCurrent />
          <ColBuild />
          {PREPROD_REGION_COLUMNS.map(r => (
            <ColPreprodRegion key={`preprod-${r}`} />
          ))}
          {REGION_COLUMNS.map(r => (
            <ColRegion key={r} />
          ))}
          <ColOwner />
        </colgroup>
        <thead>
          <tr>
            <th>Step</th>
            <th>State</th>
            <th>Current</th>
            <th>Build / Release</th>
            {PREPROD_REGION_COLUMNS.map(r => (
              <RegionHeader key={`preprod-${r}`}>{r}-PP</RegionHeader>
            ))}
            {REGION_COLUMNS.map(r => (
              <RegionHeader key={r}>{r}</RegionHeader>
            ))}
            <th>Owner</th>
          </tr>
        </thead>
        <tbody>
          {steps.length === 0 ? (
            <tr>
              <td
                colSpan={
                  4 + PREPROD_REGION_COLUMNS.length + REGION_COLUMNS.length + 1
                }
              >
                <Empty>
                  No rows in this section — click "Add manual step" to add one.
                </Empty>
              </td>
            </tr>
          ) : (
            steps.map((row, idx) => {
              const { isParked, tint, needsAttention, parkedMessage } =
                rowDisplayMeta(row, release);
              return (
                <SectionRow
                  key={row.id}
                  rowState={tint}
                  needsAttention={needsAttention}
                >
                  <td>
                    {renderStepCell(row, idx === 0, idx === steps.length - 1)}
                  </td>
                  <td>
                    {row.repo ? (
                      <>
                        <StateSelect
                          state={row.state}
                          onChange={next => props.onChangeState(row.id, next)}
                          disabled={editing}
                        />
                        {row.stateUpdatedBy && (
                          <span>
                            by <EntityRefLink entityRef={row.stateUpdatedBy} />
                          </span>
                        )}
                      </>
                    ) : (
                      <RegionDash>—</RegionDash>
                    )}
                  </td>
                  <td>
                    <CurrentCell row={row} />
                  </td>
                  {isParked ? (
                    // Collapse Build + preprod cells + 6 region cells into a
                    // single muted message so the row reads as "intentionally
                    // parked" rather than "missing data".
                    <RowParkedMessage
                      colSpan={
                        1 + PREPROD_REGION_COLUMNS.length + REGION_COLUMNS.length
                      }
                    >
                      {parkedMessage}
                    </RowParkedMessage>
                  ) : (
                    <>
                      <td>
                        <BuildCell row={row} release={release} />
                      </td>
                      {PREPROD_REGION_COLUMNS.map(r => (
                        <td
                          key={`preprod-${r}`}
                          style={{ textAlign: 'center' }}
                        >
                          <PreprodRegionCell
                            row={row}
                            region={r}
                            release={release}
                          />
                        </td>
                      ))}
                      {REGION_COLUMNS.map(r => (
                        <td
                          key={r}
                          style={{ textAlign: 'center' }}
                        >
                          <RegionCell
                            row={row}
                            region={r}
                            env={env}
                            release={release}
                            editing={editing}
                            onToggleRegion={
                              editing
                                ? (props as EditProps).onToggleRegion
                                : undefined
                            }
                          />
                        </td>
                      ))}
                    </>
                  )}
                  <td>
                    {row.owner ? (
                      <EntityRefLink entityRef={row.owner} />
                    ) : (
                      <OwnerMissing>—</OwnerMissing>
                    )}
                  </td>
                </SectionRow>
              );
            })
          )}
        </tbody>
      </Table>
    );

  return (
    <SectionCard editing={editing}>
      {header}
      {lockedByOther && props.mode === 'view' && props.lockedBy && (
        <LockedBanner>
          <LockIcon fontSize="small" />
          <span>
            Locked by <EntityRefLink entityRef={props.lockedBy} /> — view only
            until they Save or Cancel.
          </span>
        </LockedBanner>
      )}
      {body}
      {editing && (
        <AddRow>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={(props as EditProps).onAddManualStep}
          >
            Add manual step
          </Button>
        </AddRow>
      )}
    </SectionCard>
  );
};
